import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]} ${spacing[3]};
  margin-top: ${spacing[3]};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: ${borders.radius.full};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
`;

const ProfileLink = styled(Link)`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export interface CompactIdentityMetaProps {
  badge?: string;
  chips?: string[];
  profilePath?: string;
}

/**
 * Compact header meta: membership badge + optional chips + profile link.
 * Name stays in the greeting — avoid repeating full name / email.
 */
export const CompactIdentityMeta: React.FC<CompactIdentityMetaProps> = ({
  badge,
  chips = [],
  profilePath = '/profile',
}) => {
  const { t } = useTranslation();
  const extras = chips.map(chip => chip.trim()).filter(Boolean);
  return (
    <Meta>
      {badge && <Badge>{badge}</Badge>}
      {extras.map(chip => (
        <Badge key={chip}>{chip}</Badge>
      ))}
      <ProfileLink to={profilePath}>{t('dashboard.viewProfileLink')}</ProfileLink>
    </Meta>
  );
};

export default CompactIdentityMeta;
